<template>
  <div class="container">
    <div class="mt-5">
      <h2>User</h2>
      <conflict-resolver />
      <p>Name: {{ user.name }}</p>
      <p>Email: {{ user.email }}</p>
      <p>ID: {{ user.identifier }}</p>
      <p>Role: {{ user.display_role }}</p>
    </div>

    <div class="flex mt-3 flex-column">
      <h2>App Settings</h2>
      <div class="flex flex-wrap flex-column mx-list-4 mt-1">
        <div class="flex align-items-center">
          <LabelledToggle
            v-model="flags.openPumpTableByDefault"
            :disabled="isSettingsDisabled"
          />
          <label for="pump-table-open" class="ml-1"
            >Open pump table by default
          </label>
        </div>
        <div class="flex align-items-center mt-3">
          <LabelledToggle
            v-model="flags.openSideCalcsByDefault"
            :disabled="isSettingsDisabled"
          />
          <label for="side-calcs-open" class="ml-1"
            >Open side calculations by default
          </label>
        </div>
        <div class="flex align-items-center mt-3">
          <LabelledToggle
            v-model="flags.displayHpsOnly"
            :disabled="isSettingsDisabled"
          />
          <label class="ml-1">Display HPS Only</label>
        </div>
        <div class="flex align-items-center mt-3">
          <PumpFrequencyRange
            id="pump-frequency-range"
            v-model="pumpFrequencyRange"
            class="mt-3"
            :motor-poles="[1]"
          />
          <label for="pump-frequency-range" class="ml-2"
            >Default frequency range for pump table
          </label>
        </div>
        <div class="col-12 mt-4 bg-gray-100">
          <div class="flex flex-row" style="align-items: baseline">
            <h3 class="mt-0">Side Calcs</h3>
            <p class="mt-0 ml-4">
              (The order in which you select will mirror the order displayed)
            </p>
          </div>
          <div class="grid">
            <MultiSelect
              :model-value="sideCalcState"
              placeholder="Select calculators..."
              display="chip"
              scroll-height="50vh"
              option-label="label"
              option-value="id"
              :options="filteredEquipmentCalculatorSections"
              @update:model-value="setActiveSideCalcState($event)"
            />
          </div>
        </div>
      </div>
      <h3 class="mt-5">Formats</h3>
      <div class="flex flex-wrap mx-list-4 mt-1">
        <div class="flex align-items-center">
          <LabelledToggle
            v-model="flags.enforceUsNumberFormatting"
            input-id="enforce-us-number-formatting"
            :disabled="isSettingsDisabled"
          />
          <label for="enforce-us-number-formatting" class="ml-1">
            Enforce US Number Formatting
          </label>
        </div>
      </div>
    </div>
    <div class="flex align-items-center mt-3">
      <LabelledToggle v-model="isDefaultHps" :disabled="isSettingsDisabled" />
      <label class="ml-1"> Default HPS Pumping Type </label>
    </div>
    <div class="flex align-items-center mt-5">
      <h2>Storage</h2>
      <Button class="ml-4 p-button-danger" @click="clearUrqlCache"
        >Clear Offline Storage <icon class="ml-2" :icon="['fas', 'trash']"
      /></Button>
    </div>
    <p>Using {{ usage }}MB of {{ quota }}MB</p>
    <progress-bar
      :value="(usage / quota) * 100 < 1 ? 1 : (usage / quota) * 100"
    />
  </div>
</template>

<script setup lang="ts">
import useUserInfo from "@/composables/auth/useUserInfo";
import type { Ref } from "vue";
import { onMounted, ref, computed, watch } from "vue";
import ProgressBar from "primevue/progressbar";
import ConflictResolver from "@/components/update_conflict/ConflictResolver.vue";
import { urqlIdbStorage } from "@/composables/exchanges/graphcache";
import { useToast } from "primevue/usetoast";
import { clear } from "idb-keyval";
import { clearPersistedTornadoResults } from "@/libs/charting/tornado/worker/tornadoResultCache";
import LabelledToggle from "@/components/LabelledToggle.vue";
import MultiSelect from "primevue/multiselect";
import { useSettingsStore } from "@/store";
import PumpFrequencyRange from "@/components/scenarios/charts/PumpFrequencyRange.vue";
import {
  EquipmentWithNoCalculations,
  HIDDEN_WHEN_ESP,
  HIDDEN_WHEN_HPS,
  ScenarioDetailSectionTitle
} from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { LS_KEY } from "@/libs/calculations/constants";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const {
  flags,
  // toggleFlag,
  setMinimumPumpChartFrequency,
  setMaximumPumpChartFrequency,
  sideCalcState,
  setActiveSideCalcState
} = useSettingsStore();

interface Estimate {
  quota?: number;
  usage?: number;
}

const user = useUserInfo();

// Calculate space being used in indexedDb
const quota: Ref<number> = ref(0);
const usage: Ref<number> = ref(0);

// App Settings
const isSettingsDisabled: Ref<boolean> = ref(false);

const equipmentCalculatorSectionOptions: Ref<{ id: string; label: string }[]> =
  ref([
    {
      id: EquipmentCalculatorSectionKey.PUMP,
      label: ScenarioDetailSectionTitle.PUMP
    },
    {
      id: EquipmentCalculatorSectionKey.MOTOR,
      label: ScenarioDetailSectionTitle.MOTOR
    },
    {
      id: EquipmentCalculatorSectionKey.HPS_MOTOR,
      label: ScenarioDetailSectionTitle.HPS_MOTOR
    },
    {
      id: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
      label: ScenarioDetailSectionTitle.INTAKE
    },
    {
      id: EquipmentCalculatorSectionKey.CABLE,
      label: ScenarioDetailSectionTitle.CABLE
    },
    {
      id: EquipmentCalculatorSectionKey.FLUID_PROPERTIES,
      label: ScenarioDetailSectionTitle.FLUID_PROPERTIES
    },
    {
      id: EquipmentCalculatorSectionKey.WELL_TEST,
      label: ScenarioDetailSectionTitle.WELL_TEST
    },
    {
      id: EquipmentCalculatorSectionKey.SEAL,
      label: ScenarioDetailSectionTitle.SEAL_SENSOR_MLE
    },
    {
      id: EquipmentCalculatorSectionKey.THRUST_CHAMBER,
      label: ScenarioDetailSectionTitle.THRUST_CHAMBER
    },
    {
      id: EquipmentCalculatorSectionKey.MLE,
      label: ScenarioDetailSectionTitle.MLE
    },
    {
      id: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
      label: ScenarioDetailSectionTitle.SURFACE_DRIVE
    }
  ]);

function calcStorage(): void {
  window.navigator.storage.estimate().then((estimate: Estimate) => {
    quota.value = Number(((estimate.quota || 0) / 1_000_000).toFixed(2));
    usage.value = Number(((estimate.usage || 0) / 1_000_000).toFixed(2));
  });
}

const toaster = useToast();

const isDefaultHps = computed<boolean>({
  get() {
    return defaultPumpingType.value === Sizing_Pumping_Type_Enum.Hps;
  },
  set(val: boolean) {
    defaultPumpingType.value = val
      ? Sizing_Pumping_Type_Enum.Hps
      : Sizing_Pumping_Type_Enum.EspOnShore;
    // Not a good pattern to have side effects in a computed setter,
    // but this is a simple way to ensure state is reset
    sideCalcState.value = [];
  }
});

async function clearUrqlCache(): Promise<void> {
  await urqlIdbStorage.clear();
  await clear();
  await clearPersistedTornadoResults();
  calcStorage();
  toaster.add({
    severity: "info",
    summary: "Offline Cache Cleared",
    detail: "All offline data has been reset",
    life: 5000
  });
}

const defaultPumpingType = ref<
  Sizing_Pumping_Type_Enum.Hps | Sizing_Pumping_Type_Enum.EspOnShore
>(Sizing_Pumping_Type_Enum.EspOnShore);

// const enforceNumberFormatting = () => {
//   toggleFlag("enforceUsNumberFormatting");
// };

const filteredEquipmentCalculatorSections = computed(() => {
  return equipmentCalculatorSectionOptions.value.filter(section => {
    if (
      EquipmentWithNoCalculations.includes(
        section.id as EquipmentCalculatorSectionKey
      )
    ) {
      return false;
    }
    // Hide sections based on the default pumping type
    if (
      (defaultPumpingType.value === Sizing_Pumping_Type_Enum.Hps &&
        HIDDEN_WHEN_HPS.includes(
          section.id as EquipmentCalculatorSectionKey
        )) ||
      (defaultPumpingType.value === Sizing_Pumping_Type_Enum.EspOnShore &&
        HIDDEN_WHEN_ESP.includes(section.id as EquipmentCalculatorSectionKey))
    ) {
      return false;
    }
    return true;
  });
});

onMounted(() => {
  calcStorage();
  const saved = localStorage.getItem(LS_KEY) as
    | Sizing_Pumping_Type_Enum.Hps
    | Sizing_Pumping_Type_Enum.EspOnShore
    | null;
  if (
    saved === Sizing_Pumping_Type_Enum.Hps ||
    saved === Sizing_Pumping_Type_Enum.EspOnShore
  )
    defaultPumpingType.value = saved;
});

const pumpFrequencyRange = computed<[number, number]>({
  get() {
    return [
      flags.value.minimumPumpChartFrequency,
      flags.value.maximumPumpChartFrequency
    ];
  },
  set([minVal, maxVal]) {
    setMinimumPumpChartFrequency(minVal);
    setMaximumPumpChartFrequency(maxVal);
  }
});

watch(defaultPumpingType, val => {
  localStorage.setItem(LS_KEY, val);
});
</script>

<style lang="scss" scoped>
#app-settings-container {
  flex-direction: column;
}
</style>
